// MACHINERY V2 (Stroje) — Live Fleet header strip (M2.2).
//
// Spec: MACHINERY_V2_IMPLEMENTATION_TICKET.md §8 M2.2. Reference impl:
// design_handoff_machinery_v3/machinery-live.jsx (LiveFleetStrip).
//
// A counter per canonical card state; clicking one TOGGLES that state in
// `fleetStatusFilters` and the grid filters client-side. It shares its
// vocabulary with the FilterRail through fleetStatusFilters.ts, so the two can
// never offer different sets.
//
// COUNTS COME FROM THE CONTRACT. The `counts` prop is built by
// fleetSelectors.countStates() from states already resolved by
// deriveCardPresentation — this component never sees raw telemetry and cannot
// count `alarms.length` behind the state machine's back. A machine appears in
// EXACTLY ONE cell (the artboard's mock double-counts stale machines under both
// their status and a "stale data" cell; a toggle cannot do that and stay
// unambiguous — see fleetStatusFilters.ts).
//
// The cells WRAP rather than squeeze: nine counters plus the header do not fit
// one desktop row beside a 232px rail, and a squeezed counter ellipsises its
// own label. Wrapping keeps every state readable at tablet width too, which is
// the M2.11 tier this sub-phase must not hard-break.
//
// No Tailwind, no shadcn, no MUI, no @emotion, no sx, no dt tokens.
import React from "react";

import useTranslation from "../../../../shared/hooks/useTranslation";
import ConnDot from "./ConnDot";
import StatusIcon from "./StatusIcon";
import { MachineryV2CardState } from "./cardStates";
import { FLEET_STATE_FILTERS, filterInk } from "./fleetStatusFilters";

export interface LiveFleetStripProps {
  total: number;
  connected: number;
  counts: Record<string, number>;
  selected: MachineryV2CardState[];
  onToggle: (state: MachineryV2CardState) => void;
}

const LiveFleetStrip: React.FC<LiveFleetStripProps> = ({
  total, connected, counts, selected, onToggle,
}) => {
  const { translate } = useTranslation("myMachinery");

  return (
    <div
      style={{
        display: "flex", alignItems: "stretch", flexWrap: "wrap",
        background: "var(--card)", border: "1px solid var(--border)",
        borderRadius: 12, overflow: "hidden", marginBottom: 14,
      }}
    >
      <div
        style={{
          padding: "12px 16px", borderRight: "1px solid var(--border)",
          background: "var(--mod-accent-soft)", display: "flex",
          flexDirection: "column", justifyContent: "center", minWidth: 176,
          flexGrow: 1,
        }}
      >
        <div
          style={{
            fontFamily: "var(--font-mono)", fontSize: 10, color: "var(--mod-accent)",
            letterSpacing: "0.14em", textTransform: "uppercase", fontWeight: 700,
            display: "flex", alignItems: "center", gap: 6,
          }}
        >
          {/* Decorative "the fleet view is live" pip — NOT a machine's dot. */}
          <ConnDot state="live" size={6} />
          {translate("myMachinery:v2.fleet.liveFleet")}
        </div>
        <div style={{ fontSize: 12, color: "var(--muted)", marginTop: 4 }}>
          {translate("myMachinery:v2.fleet.machineCount", { count: total })}
          {" · "}
          {translate("myMachinery:v2.fleet.connectedCount", { count: connected })}
        </div>
      </div>

      {FLEET_STATE_FILTERS.map((cell) => {
        const active = selected.indexOf(cell.state) >= 0;
        const ink = filterInk(cell.token);
        const value = counts[cell.state] || 0;
        return (
          <button
            key={cell.state}
            type="button"
            aria-pressed={active}
            onClick={() => onToggle(cell.state)}
            title={translate(cell.labelKey)}
            style={{
              flex: "1 1 120px", minWidth: 120, padding: "10px 14px",
              background: active ? "var(--mod-accent-soft)" : "transparent",
              border: 0, borderRight: "1px solid var(--border-soft)",
              borderBottom: active
                ? "2px solid var(--mod-accent)"
                : "2px solid transparent",
              cursor: "pointer", textAlign: "left", fontFamily: "inherit",
              display: "flex", flexDirection: "column", justifyContent: "center",
              gap: 2, minHeight: 62,
            }}
          >
            <span style={{ display: "flex", alignItems: "baseline", gap: 6 }}>
              <span
                style={{
                  fontFamily: "var(--font-mono)", fontSize: 21, fontWeight: 700,
                  color: value > 0 ? ink : "var(--faint)", letterSpacing: "-0.01em",
                }}
              >
                {String(value).padStart(2, "0")}
              </span>
              {cell.icon && (
                <span style={{ color: value > 0 ? ink : "var(--faint)", opacity: 0.7 }}>
                  <StatusIcon kind={cell.icon} size={11} />
                </span>
              )}
            </span>
            <span
              style={{
                fontSize: 11, color: "var(--muted)", letterSpacing: "0.02em",
                whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
              }}
            >
              {translate(cell.labelKey)}
            </span>
          </button>
        );
      })}
    </div>
  );
};

export default LiveFleetStrip;
